import { useCallback, useEffect, useRef, useState } from 'react';

import { useApolloClient } from '@apollo/client';

import { isAuthenticatedUser } from 'js/lib/user';

import type { ValidPhone } from 'bundles/expression-of-interest/components/i18n-phone-number-input/types';
import { newValidPhone } from 'bundles/expression-of-interest/components/i18n-phone-number-input/utils';
import { getUserPhone } from 'bundles/expression-of-interest/utils/util';

/**
 * Prefills a logged-in visitor's phone from their learner profile.
 * The prefill is dropped if the visitor has already typed a number by the time it resolves
 */
export const usePrefillPhoneFromProfile = (): [ValidPhone | undefined, (phone?: ValidPhone) => void, boolean] => {
  const [phone, setPhone] = useState<ValidPhone | undefined>(undefined);
  const [hasHydrated, setHasHydrated] = useState(false);
  const hasUserEdited = useRef(false);
  const apolloClient = useApolloClient();

  const setPhoneFromUser = useCallback<(value?: ValidPhone) => void>((value) => {
    if (value !== undefined) {
      hasUserEdited.current = true;
    }
    setPhone(value);
  }, []);

  useEffect(() => {
    if (!isAuthenticatedUser()) {
      return undefined;
    }

    let isMounted = true;
    getUserPhone(apolloClient)
      .then((existingPhone) => {
        const validPhone = existingPhone && newValidPhone(existingPhone);
        if (isMounted && validPhone && !hasUserEdited.current) {
          setPhone(validPhone);
          setHasHydrated(true);
        }
      })
      .catch(() => {
        // Prefill is best-effort; leave the field blank on failure.
      });

    return () => {
      isMounted = false;
    };
  }, [apolloClient]);

  return [phone, setPhoneFromUser, hasHydrated];
};

export default usePrefillPhoneFromProfile;
